import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CloudIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  EnvelopeIcon,
  LinkIcon,
  ChatBubbleLeftRightIcon,
  BugAntIcon,
  CurrencyPoundIcon,
  ChartBarIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { integrationsAPI, realIntegrationsAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const INTEGRATION_TYPES = [
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team communication and notifications',
    icon: '💬',
    color: 'purple',
    supportsOAuth: true,
    actions: ['test', 'send_message', 'oauth'],
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Code repository and version control',
    icon: '🐙',
    color: 'gray',
    actions: ['test', 'create_issue'],
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Email notifications and alerts',
    icon: '📧',
    color: 'blue',
    actions: ['test', 'send_email'],
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Accounting and financial management',
    icon: '💰',
    color: 'green',
    supportsOAuth: true,
    actions: ['test', 'oauth'],
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Issue tracking and project management',
    icon: '🎯',
    color: 'blue',
    actions: ['test'],
  },
  {
    id: 'google_drive',
    name: 'Google Drive',
    description: 'Cloud storage and document sharing',
    icon: '📁',
    color: 'green',
    actions: ['test'],
  },
  {
    id: 'microsoft_teams',
    name: 'Microsoft Teams',
    description: 'Team collaboration and meetings',
    icon: '👥',
    color: 'indigo',
    actions: ['test'],
  },
  {
    id: 'asana',
    name: 'Asana',
    description: 'Work management and task tracking',
    icon: '✓',
    color: 'pink',
    actions: ['test'],
  },
  // Custom Integrations
  {
    id: 'custom_crm',
    name: 'Custom CRM',
    description: 'Your proprietary CRM system',
    icon: '🎯',
    color: 'cyan',
    isCustom: true,
    actions: ['test', 'sync_data'],
  },
  {
    id: 'executive_force_ai',
    name: 'Executive Force AI',
    description: 'AI-powered executive insights',
    icon: '🤖',
    color: 'violet',
    isCustom: true,
    actions: ['test', 'get_insights'],
  },
  {
    id: 'financial_app_uk',
    name: 'Financial-App-UK',
    description: 'UK financial services integration',
    icon: '£',
    color: 'emerald',
    isCustom: true,
    actions: ['test', 'sync_transactions'],
  },
];

export default function Integrations() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [configData, setConfigData] = useState({});
  
  // Action modals
  const [showSlackModal, setShowSlackModal] = useState(false);
  const [showGitHubModal, setShowGitHubModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [slackMessage, setSlackMessage] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [githubIssueTitle, setGithubIssueTitle] = useState('');
  const [githubIssueBody, setGithubIssueBody] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Fetch integrations
  const { data: integrations, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsAPI.list(),
    retry: false,
  });

  // Add integration mutation
  const addMutation = useMutation({
    mutationFn: (data) => integrationsAPI.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration added successfully!');
      setShowAddModal(false);
      setSelectedType(null);
      setConfigData({});
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add integration');
    },
  });

  // Toggle integration mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }) => integrationsAPI.toggle(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration updated successfully!');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update integration');
    },
  });

  // Delete integration mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => integrationsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration deleted successfully!');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete integration');
    },
  });

  // Test integration mutation
  const testMutation = useMutation({
    mutationFn: (id) => integrationsAPI.test(id),
    onSuccess: (data) => {
      if (data.status === 'success') {
        let message = data.message;
        if (data.details) {
          const details = Object.entries(data.details)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          message += ` (${details})`;
        }
        toast.success(message, { duration: 5000 });
      } else {
        toast.warning(data.message || 'Test completed with warnings');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || error.message || 'Integration test failed');
    },
  });

  const handleAddIntegration = () => {
    if (!selectedType) {
      toast.error('Please select an integration type');
      return;
    }

    const integrationData = {
      name: INTEGRATION_TYPES.find(t => t.id === selectedType)?.name || selectedType,
      integration_type: selectedType,
      config: configData,
      enabled: true,
    };

    addMutation.mutate(integrationData);
  };

  const handleToggle = (integration) => {
    toggleMutation.mutate({
      id: integration.id,
      enabled: !integration.enabled,
    });
  };

  const handleDelete = (integration) => {
    if (window.confirm(`Are you sure you want to delete ${integration.name}?`)) {
      deleteMutation.mutate(integration.id);
    }
  };

  const handleTest = (integration) => {
    testMutation.mutate(integration.id);
  };

  // OAuth handler
  const handleOAuth = (integrationType) => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const oauthUrl = `${baseUrl}/api/oauth/${integrationType}/authorize`;
    window.location.href = oauthUrl;
  };

  // Action mutation for real integrations
  const actionMutation = useMutation({
    mutationFn: async ({ action, data }) => {
      const response = await realIntegrationsAPI[action](data);
      return response;
    },
    onSuccess: (data, variables) => {
      if (variables.action === 'sendSlackNotification') {
        toast.success('Slack message sent successfully!');
        setShowSlackModal(false);
        setSlackMessage('');
        setSlackChannel('');
      } else if (variables.action === 'createGitHubIssue') {
        toast.success(`GitHub issue created: #${data.issue_number}`);
        setShowGitHubModal(false);
        setGithubIssueTitle('');
        setGithubIssueBody('');
      } else if (variables.action === 'sendEmail') {
        toast.success('Email sent successfully!');
        setShowEmailModal(false);
        setEmailTo('');
        setEmailSubject('');
        setEmailBody('');
      }
    },
    onError: (error, variables) => {
      toast.error(error.message || `Failed to execute ${variables.action}`);
    },
  });

  const getIntegrationType = (typeId) => {
    return INTEGRATION_TYPES.find(t => t.id === typeId) || {
      id: typeId,
      name: typeId,
      description: 'Custom integration',
      icon: '🔌',
      color: 'gray',
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="text-gray-600">Connect external services and tools</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Integration
        </button>
      </div>

      {/* Connected Integrations */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Connected Integrations</h2>
        </div>
        
        {integrations && integrations.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {integrations.map((integration) => {
              const type = getIntegrationType(integration.integration_type);
              return (
                <div key={integration.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 bg-${type.color}-100 rounded-lg flex items-center justify-center text-2xl`}>
                        {type.icon}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{integration.name}</h3>
                        <p className="text-sm text-gray-500">{type.description}</p>
                        <div className="flex items-center space-x-4 mt-1">
                          {integration.enabled ? (
                            <span className="inline-flex items-center text-xs text-green-700">
                              <CheckCircleIcon className="w-4 h-4 mr-1" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs text-gray-500">
                              <XCircleIcon className="w-4 h-4 mr-1" />
                              Inactive
                            </span>
                          )}
                          {integration.last_synced && (
                            <span className="text-xs text-gray-500">
                              Last synced: {new Date(integration.last_synced).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      {/* OAuth Connect Button */}
                      {type.supportsOAuth && (
                        <button
                          onClick={() => handleOAuth(type.id)}
                          className="px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-1"
                          title="Connect via OAuth"
                        >
                          <LinkIcon className="w-4 h-4" />
                          Connect OAuth
                        </button>
                      )}
                      
                      {/* Slack Actions */}
                      {integration.integration_type === 'slack' && integration.enabled && (
                        <button
                          onClick={() => setShowSlackModal(true)}
                          className="px-3 py-2 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors flex items-center gap-1"
                          title="Send Test Message"
                        >
                          <ChatBubbleLeftRightIcon className="w-4 h-4" />
                          Send Message
                        </button>
                      )}
                      
                      {/* GitHub Actions */}
                      {integration.integration_type === 'github' && integration.enabled && (
                        <button
                          onClick={() => setShowGitHubModal(true)}
                          className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
                          title="Create Issue"
                        >
                          <BugAntIcon className="w-4 h-4" />
                          Create Issue
                        </button>
                      )}
                      
                      {/* Email Actions */}
                      {integration.integration_type === 'email' && integration.enabled && (
                        <button
                          onClick={() => setShowEmailModal(true)}
                          className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1"
                          title="Send Email"
                        >
                          <EnvelopeIcon className="w-4 h-4" />
                          Send Email
                        </button>
                      )}
                      
                      {/* Custom CRM Actions */}
                      {integration.integration_type === 'custom_crm' && integration.enabled && (
                        <button
                          onClick={() => toast.success('CRM sync initiated!')}
                          className="px-3 py-2 text-xs font-medium text-cyan-700 bg-cyan-100 rounded-lg hover:bg-cyan-200 transition-colors flex items-center gap-1"
                          title="Sync CRM Data"
                        >
                          <ChartBarIcon className="w-4 h-4" />
                          Sync Data
                        </button>
                      )}
                      
                      {/* Executive Force AI Actions */}
                      {integration.integration_type === 'executive_force_ai' && integration.enabled && (
                        <button
                          onClick={() => toast.success('Fetching AI insights...')}
                          className="px-3 py-2 text-xs font-medium text-violet-700 bg-violet-100 rounded-lg hover:bg-violet-200 transition-colors flex items-center gap-1"
                          title="Get AI Insights"
                        >
                          <SparklesIcon className="w-4 h-4" />
                          Get Insights
                        </button>
                      )}
                      
                      {/* Financial-App-UK Actions */}
                      {integration.integration_type === 'financial_app_uk' && integration.enabled && (
                        <button
                          onClick={() => toast.success('Syncing transactions...')}
                          className="px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors flex items-center gap-1"
                          title="Sync Transactions"
                        >
                          <CurrencyPoundIcon className="w-4 h-4" />
                          Sync £
                        </button>
                      )}
                      
                      {/* Management Buttons */}
                      <button
                        onClick={() => handleTest(integration)}
                        disabled={testMutation.isPending}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Test Connection"
                      >
                        <CloudIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleToggle(integration)}
                        disabled={toggleMutation.isPending}
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                        title={integration.enabled ? 'Disable' : 'Enable'}
                      >
                        <Cog6ToothIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(integration)}
                        disabled={deleteMutation.isPending}
                        className="p-2 text-gray-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CloudIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations yet</h3>
            <p className="text-gray-500 mb-6">Connect your first integration to get started</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Integration
            </button>
          </div>
        )}
      </div>

      {/* Available Integrations */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Available Integrations</h2>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {INTEGRATION_TYPES.map((type) => (
            <div
              key={type.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => {
                setSelectedType(type.id);
                setShowAddModal(true);
              }}
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className={`w-10 h-10 bg-${type.color}-100 rounded-lg flex items-center justify-center text-xl`}>
                  {type.icon}
                </div>
                <h3 className="text-sm font-medium text-gray-900">{type.name}</h3>
              </div>
              <p className="text-xs text-gray-500">{type.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Add Integration Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Integration</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Integration Type
                </label>
                <select
                  value={selectedType || ''}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select integration type...</option>
                  {INTEGRATION_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.icon} {type.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedType && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Key / Token
                    </label>
                    <input
                      type="password"
                      placeholder="Enter your API key or token"
                      value={configData.api_key || ''}
                      onChange={(e) => setConfigData({ ...configData, api_key: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook URL (Optional)
                    </label>
                    <input
                      type="url"
                      placeholder="https://example.com/webhook"
                      value={configData.webhook_url || ''}
                      onChange={(e) => setConfigData({ ...configData, webhook_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedType(null);
                  setConfigData({});
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddIntegration}
                disabled={!selectedType || addMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {addMutation.isPending ? 'Adding...' : 'Add Integration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slack Message Modal */}
      {showSlackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Send Slack Message</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel
                </label>
                <input
                  type="text"
                  placeholder="#general"
                  value={slackChannel}
                  onChange={(e) => setSlackChannel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  rows={4}
                  placeholder="Enter your message..."
                  value={slackMessage}
                  onChange={(e) => setSlackMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSlackModal(false);
                  setSlackMessage('');
                  setSlackChannel('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => actionMutation.mutate({
                  action: 'sendSlackNotification',
                  data: { message: slackMessage, channel: slackChannel }
                })}
                disabled={!slackMessage || !slackChannel || actionMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {actionMutation.isPending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GitHub Issue Modal */}
      {showGitHubModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create GitHub Issue</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Title
                </label>
                <input
                  type="text"
                  placeholder="Bug: Something is broken"
                  value={githubIssueTitle}
                  onChange={(e) => setGithubIssueTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Body
                </label>
                <textarea
                  rows={6}
                  placeholder="Describe the issue..."
                  value={githubIssueBody}
                  onChange={(e) => setGithubIssueBody(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGitHubModal(false);
                  setGithubIssueTitle('');
                  setGithubIssueBody('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => actionMutation.mutate({
                  action: 'createGitHubIssue',
                  data: { title: githubIssueTitle, body: githubIssueBody }
                })}
                disabled={!githubIssueTitle || !githubIssueBody || actionMutation.isPending}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
              >
                {actionMutation.isPending ? 'Creating...' : 'Create Issue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Send Email</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To (comma-separated emails)
                </label>
                <input
                  type="text"
                  placeholder="user@example.com, team@example.com"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  placeholder="Project Update"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  rows={6}
                  placeholder="Enter your message..."
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailTo('');
                  setEmailSubject('');
                  setEmailBody('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => actionMutation.mutate({
                  action: 'sendEmail',
                  data: {
                    to_emails: emailTo.split(',').map(e => e.trim()),
                    subject: emailSubject,
                    body: emailBody
                  }
                })}
                disabled={!emailTo || !emailSubject || !emailBody || actionMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {actionMutation.isPending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
